import React, { useEffect, useState } from 'react';
import { FiMail, FiArrowRight, FiRefreshCw, FiEye } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import styles from '../../pages/Login/Login.module.css';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { verifyOtp, resendOtp, clearPendingVerification } from '../../features/auth/authSlice';
import { useTheme } from '../../app/ThemeContext';

interface Props {
  email: string;
}

const DEV_OTP_VISIBLE_SECONDS = 45;

const OtpVerify: React.FC<Props> = ({ email }) => {
  const [otp, setOtp] = useState('');
  const [resent, setResent] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(DEV_OTP_VISIBLE_SECONDS);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { status, error, otpMessage, devOtp } = useAppSelector((state) => state.auth);
  const { setTheme } = useTheme();

  useEffect(() => {
    if (!devOtp) return;
    setSecondsLeft(DEV_OTP_VISIBLE_SECONDS);
    const interval = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(interval);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [devOtp]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await dispatch(verifyOtp({ email, otp }));
    if (verifyOtp.fulfilled.match(result)) {
      setTheme('dark');
      const role = result.payload.user.role;
      navigate(role === 'admin' ? '/admin' : '/dashboard');
    }
  };

  const handleResend = async () => {
    setResent(true);
    await dispatch(resendOtp({ email }));
    setTimeout(() => setResent(false), 20000);
  };

  return (
    <div className={styles.card}>
      <div className={styles.eyebrow}>BCT Platform</div>
      <h1 className={styles.title}>Verify your email</h1>
      <p className={styles.subtitle}>
        We sent a 6-digit code to <b style={{ color: 'var(--cream)' }}>{email}</b>. Enter it below to activate your account.
      </p>

      {devOtp && secondsLeft > 0 && (
        <div
          style={{
            background: 'rgba(232,168,106,.1)',
            border: '1px solid rgba(232,168,106,.35)',
            borderRadius: 10,
            padding: '12px 14px',
            marginBottom: 16,
            fontSize: 12.5,
            color: 'var(--cream)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--copper-light)', fontWeight: 700, marginBottom: 4 }}>
            <FiEye size={13} /> Dev mode — email isn't configured yet
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <span>
              Your code: <span style={{ fontSize: 18, fontWeight: 700, letterSpacing: 4 }}>{devOtp}</span>
            </span>
            <button
              type="button"
              onClick={() => setOtp(devOtp)}
              style={{
                background: 'var(--copper)', color: '#ffffff', border: 'none', borderRadius: 6,
                padding: '5px 10px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', flexShrink: 0,
              }}
            >
              Use this code
            </button>
          </div>
          <div style={{ color: 'var(--muted)', marginTop: 6 }}>Hides in {secondsLeft}s (dev builds only — never shown once real email is set up)</div>
        </div>
      )}

      {error && <div className={styles.error}>{error}</div>}
      {otpMessage && !error && (
        <div
          className={styles.error}
          style={{ background: 'rgba(90,168,120,.1)', borderColor: 'rgba(90,168,120,.3)', color: 'var(--success)' }}
        >
          {otpMessage}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className={styles.field}>
          <label className={styles.label}>Verification code</label>
          <div className={styles.inputWrap}>
            <FiMail className={styles.inputIcon} />
            <input
              className={styles.input}
              placeholder="6-digit code"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              maxLength={6}
              required
              autoFocus
            />
          </div>
        </div>

        <button type="submit" className={styles.submitBtn} disabled={status === 'loading' || otp.length !== 6}>
          {status === 'loading' ? <span className={styles.spinner} /> : <>Verify &amp; continue <FiArrowRight /></>}
        </button>
      </form>

      <div className={styles.footer}>
        Didn't get it?{' '}
        <button
          type="button"
          onClick={handleResend}
          disabled={resent}
          style={{ background: 'none', border: 'none', padding: 0, cursor: resent ? 'default' : 'pointer', color: 'var(--copper-light)', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          <FiRefreshCw size={12} /> {resent ? 'Sent — check again' : 'Resend code'}
        </button>
      </div>

      <div className={styles.footer} style={{ marginTop: 8 }}>
        <button
          type="button"
          onClick={() => dispatch(clearPendingVerification())}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--muted)' }}
        >
          ← Use a different email
        </button>
      </div>
    </div>
  );
};

export default OtpVerify;
